import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { TBotItem } from '@core/interfaces/TBotItem';
import { marked } from 'marked';

import { Conversation } from '../../../infrastructure/models/Conversation';
import { TAdaptedMessageV2 } from '../../../types/types';
import { BotsService } from '@shared/services/bots.service';
import { CommonService } from '@shared/services/common.service';
import { ConversationsService } from '@shared/services/conversations.service';

import { SelectItemsToExportModalComponent } from '../export-to-selector/select-items-to-export-modal/select-items-to-export-modal.component';
import { CustomButtonComponent } from './custom-button/custom-button.component';
import { ShowOriginalResourcesComponent } from './show-original-resources/show-original-resources.component';
import { StarButtonComponent } from './star-button/star-button.component';

@Component({
    selector: 'app-message-controls',
    templateUrl: './message-controls.component.html',
    styleUrls: ['./message-controls.component.scss'],
    standalone: true,
    imports: [StarButtonComponent, CustomButtonComponent, ShowOriginalResourcesComponent, DatePipe]
})
export class MessageControlsComponent implements OnInit, OnDestroy {
  @Input() message: TAdaptedMessageV2 | null = null;
  @Input() conversation!: Conversation;
  @Input() bot: TBotItem | null = null;
  @Input() hideControls = false;
  public canStar = true;
  public currentBotName: string | null = null;
  private _subs: Subscription[] = [];

  constructor(
    private _clipboard: Clipboard,
    private _commonService: CommonService,
    protected conversationsService: ConversationsService,
    private _botsService: BotsService,
    private _dialog: MatDialog
  ) {}

  public ngOnInit(): void {
    // Subscribe to current bot observable to get bot info when fetched by ID
    this._subs.push(
      this._botsService.getCurrentBotObservable().subscribe((currentBot: TBotItem | null) => {
        if (currentBot && this.conversation?.bot?._id === currentBot._id) {
          this.currentBotName = currentBot.name;
        }
      })
    );

    // If we have a botID but no full bot object, fetch the bot info
    if (this.conversation?.bot?._id && !this.bot?.name && !this.currentBotName) {
      this._botsService.getBotById(this.conversation.bot._id, {
        suppressNotFoundRedirect: this.conversation.type === 'results',
      });
    }
  }

  public getBotName(): string {
    // Priority: direct bot prop, then current bot name, then fallback
    return this.bot?.name || this.currentBotName || 'Assistant';
  }

  isFirstAssistantMessage(message: TAdaptedMessageV2): boolean {
    if (!this.conversation?.messages) return false;

    const assistantMessages = this.conversation.messages.filter(msg => msg.author === 'assistant');
    return assistantMessages.length > 0 && assistantMessages[0] === message;
  }

  shouldShowOriginalSources(message: TAdaptedMessageV2): boolean {
    if (message.author !== 'assistant') return false;

    // Check for additional source details from stream responses
    if (message.additionalSourceDetails && message.additionalSourceDetails.length > 0) {
      return true;
    }

    // Check for original source details from initial bot init
    if (
      this.isFirstAssistantMessage(message) &&
      this.conversation?.originalSourceDetails &&
      this.conversation.originalSourceDetails.length > 0
    ) {
      return true;
    }

    return false;
  }

  getOriginalSources(message: TAdaptedMessageV2): any[] {
    return message.additionalSourceDetails || this.conversation?.originalSourceDetails || [];
  }

  public ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  async copyMessage(message: TAdaptedMessageV2) {
    // Get the raw markdown text from the message
    const markdownText = this.conversation.getMessageToString(message, true);
    const htmlContent = await this.convertMarkdownToHtml(markdownText);
    // Convert markdown to HTML for formatted copying
    this.copyToClipboardWithFormatting(htmlContent, markdownText);

    // Copy both HTML and plain text to clipboard
    if (this._clipboard.copy(message.paragraphs.join('\n'))) {
      this._commonService.openSuccessSnackBar('Copied to clipboard');
    } else {
      this._commonService.openErrorSnackBar('Failed to copy');
    }
  }

  private async convertMarkdownToHtml(markdown: string): Promise<string> {
    // First convert markdown to HTML using marked
    let htmlContent = marked.parse(markdown) as string; // TODO check if it works

    // TODO: Implement Mermaid diagram conversion for clipboard
    // For now, remove Mermaid diagrams as they don't work well in Word
    htmlContent = this.removeMermaidBlocks(htmlContent);

    return htmlContent;
  }

  // TODO: Implement proper Mermaid to SVG conversion for clipboard
  private async convertMermaidBlocksToSvg(htmlContent: string): Promise<string> {
    // This function will be implemented later when we need Mermaid support
    // For now, just return the original content
    return htmlContent;
  }

  private removeMermaidBlocks(htmlContent: string): string {
    // Remove Mermaid code blocks from HTML content
    const mermaidPatterns = [
      /<p>mermaid\s+[\s\S]*?<\/p>/g,
      /<pre><code class="language-mermaid">[\s\S]*?<\/code><\/pre>/g,
    ];

    let processedHtml = htmlContent;
    for (const pattern of mermaidPatterns) {
      processedHtml = processedHtml.replace(pattern, '');
    }

    return processedHtml;
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  private copyToClipboardWithFormatting(htmlContent: string, plainText: string): void {
    // Create a temporary element to hold the HTML content
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlContent;

    // Use the clipboard API to copy both HTML and plain text
    if (navigator.clipboard && window.ClipboardItem) {
      // Modern browsers support ClipboardItem
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      });

      navigator.clipboard
        .write([clipboardItem])
        .then(() => {
          this._commonService.openSuccessSnackBar('Copied to clipboard');
        })
        .catch(() => {
          // Fallback to plain text copy
          const copied = this._clipboard.copy(plainText);
          if (copied) {
            this._commonService.openSuccessSnackBar('Copied to clipboard');
          } else {
            this._commonService.openErrorSnackBar('Failed to copy');
          }
        });
    } else {
      // Fallback for older browsers
      const copied = this._clipboard.copy(plainText);
      if (copied) {
        this._commonService.openSuccessSnackBar('Copied to clipboard');
      } else {
        this._commonService.openErrorSnackBar('Failed to copy');
      }
    }
  }

  openExportModal(): void {
    this._dialog.open(SelectItemsToExportModalComponent, {
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: 'select-items-to-export-modal',
      hasBackdrop: true,
      disableClose: false,
    });
  }

  toggleMessageStar() {
    // Check if conversation exists
    if (!this.conversation) {
      console.warn('Cannot star conversation: conversation object is missing');
      this._commonService.openErrorSnackBar('Conversation not available. Please try again.');
      return;
    }

    // Check if conversation has ID
    if (!this.conversation.id) {
      console.warn('Cannot star conversation: conversation ID is missing');
      this._commonService.openErrorSnackBar(
        'Please wait for the conversation to be saved before starring'
      );
      return;
    }

    const toggle = () => (this.conversation!.favorite = !this.conversation!.favorite);
    const currentState = this.conversation.favorite;

    if (currentState) {
      this.conversationsService.unstarConversation(this.conversation.id, toggle);
      this._commonService.openSuccessSnackBar('Conversation removed from favorites');
    } else {
      this.conversationsService.starConversation(this.conversation.id, toggle);
      this._commonService.openSuccessSnackBar('Conversation saved to favorites');
    }
  }

  log(title: string, value: any): void {
    console.log(`# MessageControlsComponent - ${title}`, value);
  }
}
